import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Key, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MySecurity = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [changingPassword, setChangingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [deletePassword, setDeletePassword] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('As senhas não coincidem');
      return;
    }

    if (passwordData.new_password.length < 6) {
      alert('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setChangingPassword(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/customer/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('Senha alterada com sucesso!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      alert(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      alert('Por favor, informe sua senha para confirmar a exclusão');
      return;
    }

    if (!confirm('ATENÇÃO: Esta ação é IRREVERSÍVEL! Todos os seus dados serão permanentemente excluídos. Deseja realmente continuar?')) {
      return;
    }

    setDeletingAccount(true);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/customer/delete-account`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { password: deletePassword }
        }
      );

      alert('Conta excluída com sucesso. Você será redirecionado para a página inicial.');
      logout();
      navigate('/');
    } catch (error) {
      console.error('Erro ao excluir conta:', error);
      alert(error.response?.data?.detail || 'Erro ao excluir conta');
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Change Password */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Key className="w-6 h-6 text-[#00A86B]" />
          <h2 className="text-2xl font-bold text-gray-800">Alterar Senha</h2>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha Atual
            </label>
            <Input
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              required
              className="w-full"
              placeholder="Digite sua senha atual"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nova Senha
            </label>
            <Input
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              required
              minLength={6}
              className="w-full"
              placeholder="Digite sua nova senha"
            />
            <p className="text-xs text-gray-500 mt-1">Mínimo de 6 caracteres</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Nova Senha
            </label>
            <Input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              required
              className="w-full"
              placeholder="Confirme sua nova senha"
            />
          </div>

          <Button
            type="submit"
            disabled={changingPassword}
            className="bg-[#00A86B] hover:bg-[#00A86B]/90"
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Alterando...
              </>
            ) : (
              'Alterar Senha'
            )}
          </Button>
        </form>
      </Card>

      {/* Delete Account */}
      <Card className="p-6 border-red-200">
        <div className="flex items-center gap-3 mb-6">
          <Trash2 className="w-6 h-6 text-red-600" />
          <h2 className="text-2xl font-bold text-red-600">Excluir Conta</h2>
        </div>

        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>⚠️ ATENÇÃO:</strong> Esta ação é permanente e irreversível. Ao excluir sua conta:
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 mt-2 space-y-1">
              <li>Todos os seus dados pessoais serão permanentemente removidos</li>
              <li>Você perderá acesso ao histórico de pedidos</li>
              <li>Não será possível recuperar sua conta</li>
            </ul>
          </div>

          <Button
            onClick={() => setShowDeleteModal(true)}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            Excluir Minha Conta Permanentemente
          </Button>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-4">
              Confirmar Exclusão de Conta
            </h3>

            <p className="text-sm text-gray-700 mb-4">
              Para confirmar a exclusão permanente da sua conta, digite sua senha abaixo:
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Digite sua senha"
                  className="w-full"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={deletingAccount}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deletingAccount || !deletePassword}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {deletingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Excluindo...
                    </>
                  ) : (
                    'Confirmar Exclusão'
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MySecurity;
